import React, { useState } from 'react';
import styled from 'styled-components';
import { createPrivateMoneyAccountHook } from '../../actions/private-money-account-hooks';
import { ensureInteger, isNullOrUndifined } from '../../utils';
import Label from '../atoms/Label';
import Labeled from '../atoms/Labeled';
import ApiResults from '../molecules/ApiResults';
import EditMoneyAccountHook, {
  MoneyAccountHookForm,
} from '../organisms/campaign/EditMoneyAccountHook';
import MoneyAccountHookModal from './MoneyAccountHookModal';

const ModalWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    margin-top: 10px;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
    border-left: solid 1px #ececec;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px #fff;
  }
`;

const ModalButtonsWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const CreateMoneyAccountHook = () => {
  const datetimeFormat = 'YYYY/MM/DD HH:mm:ss';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hookForm, setHookForm] = useState<MoneyAccountHookForm | null>(null);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[]>([]);

  const onSubmit = (data: MoneyAccountHookForm) => {
    setHookForm(data);
    setIsModalOpen(true);
  };

  const onConfirm = () => {
    if (hookForm === null) {
      return;
    }

    const {
      privateMoney,
      pointAmount,
      name,
      description,
      senderAccount,
      bearPointAccount,
      pointExpiresAt,
      pointExpiresInDays,
      dateRange,
      usageLimit,
    } = hookForm;

    const params = {
      private_money_id: privateMoney?.id,
      point_amount: ensureInteger(pointAmount),
      name,
      description,
      sender_account_id: senderAccount?.id,
    };

    if (!isNullOrUndifined(bearPointAccount)) {
      Object.assign(params, {
        bear_point_account_id: bearPointAccount?.id,
      });
    }

    if (!isNullOrUndifined(pointExpiresAt)) {
      Object.assign(params, {
        point_expires_at: pointExpiresAt,
      });
    }

    if (!isNullOrUndifined(ensureInteger(pointExpiresInDays))) {
      Object.assign(params, {
        point_expires_in_days: (ensureInteger(pointExpiresInDays) ?? 0) + 1,
      });
    }

    if (!isNullOrUndifined(dateRange?.from)) {
      Object.assign(params, { starts_at: dateRange?.from?.toISOString() });
    }

    if (!isNullOrUndifined(dateRange?.to)) {
      Object.assign(params, { ends_at: dateRange?.to?.toISOString() });
    }

    if (!isNullOrUndifined(ensureInteger(usageLimit))) {
      Object.assign(params, {
        usage_limit: ensureInteger(usageLimit),
      });
    }

    createPrivateMoneyAccountHook(params)
      .then(() => {
        setApiMessages([
          {
            text: 'Registered a campaign: キャンペーンを登録しました',
            isError: false,
          },
        ]);
      })
      .catch((err) => {
        err.then((er) =>
          setApiMessages([
            {
              text: `Could not registered a campaign: ${er.message}.`,
              isError: true,
            },
          ])
        );
      });

    setIsModalOpen(false);
  };

  return (
    <>
      <EditMoneyAccountHook onSubmit={onSubmit} mode="create" />
      <ApiResults messages={apiMessages} />

      <MoneyAccountHookModal
        isOpen={isModalOpen}
        accountHook={hookForm}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default CreateMoneyAccountHook;
